import { useCallback } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import {
  setTikTokTrackFromSearchList,
  setTiktokTrackToInitialState,
  setTiktokTracksToInitialState,
  ExistingTikTokCampaign,
} from 'src/Redux/tiktokTrack-process'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { TikTokTrack } from 'src/Types/tracks'
import { SearchTrackFormDataProps } from 'src/Types/users'
import { usePageViewEvent } from 'src/Hooks/Analytics/useTrackEvents'
import { checkTrackToCreateCampaign } from 'src/Redux/spotifyTracks-process/api-actions'
import { searchTikTokTrackAuth } from 'src/Redux/tiktokTrack-process/api-actions'
import { getIsTikTokTrackUrl } from 'src/Constants/functions'
import {
  DEBOUNCE_TIMER_1000,
  DEBOUNCE_TIMER_300,
} from 'src/Constants/constants'
import {
  getAllSearchErrorSelector,
  getIsLoadingTikTok,
  getTiktokTrackErrorExistingTiktokCampaignSelector,
  getTikTokTracksSelector,
} from 'src/Redux/tiktokTrack-process/selectors'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { useSearchSpotifyTrack } from 'src/Redux/spotifyTracksApi/useSearchSpotifyTrack'
import {
  SpotifyTrack,
  useClearSpotifyTracksMutation,
} from 'src/Redux/spotifyTracksApi'
import {
  getSpotifyErrorSelector,
  resetSpotifyTracksState,
} from 'src/Redux/spotifyTracks-process'

import { useSearchOneTrackAuth } from './useSearchOneTrackAuth'

interface UseSearchTrackResponse {
  onClearTracks: () => void
  onCancelSearch: () => void
  onCheckTrack: (url: string) => void
  onChoseTikTokTrack: (track: TikTokTrack) => void
  onTrackSearchHandler: ({ searchInput }: SearchTrackFormDataProps) => void
  tracks: TikTokTrack[] | SpotifyTrack[]
  error?: string
  loading: boolean
  existingTikTokCampaign?: ExistingTikTokCampaign | null
  tikTokSearchErrorMessage: string
  tikTokExistingCampaign: ExistingTikTokCampaign | null
}

export const useSearchTrackAuth = (
  platform?: CampaignPlatformTypeEnum,
): UseSearchTrackResponse => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const tikTokTracks = useAppSelector(getTikTokTracksSelector)
  const searchError = useAppSelector(getAllSearchErrorSelector)
  const error = useAppSelector(getSpotifyErrorSelector)
  const existingTikTokCampaign = useAppSelector(
    getTiktokTrackErrorExistingTiktokCampaignSelector,
  )
  const isLoadingTikTok = useAppSelector(getIsLoadingTikTok)

  const {
    searchTikTokTrack,
    tikTokSearchErrorMessage,
    tikTokExistingCampaign,
  } = useSearchOneTrackAuth()

  const { enteredTrackUrlEventHandler, startedEnterTrackEventHandler } =
    usePageViewEvent()

  const [clearSpotifyTracks] = useClearSpotifyTracksMutation()

  const onClearTracks = useCallback((): void => {
    dispatch(setTiktokTracksToInitialState())
    dispatch(resetSpotifyTracksState())
    dispatch(setTiktokTrackToInitialState())
    clearSpotifyTracks()
  }, [dispatch, clearSpotifyTracks])

  const {
    searchTracks: searchSpotifyTracks,
    tracks: spotifyTracks,
    loading: isLoadingSpotify,
    error: searchErrorSpotify,
  } = useSearchSpotifyTrack()

  const searchTracks = ({
    searchInput: searchInputValue,
  }: SearchTrackFormDataProps): void => {
    if (platform === CampaignPlatformTypeEnum.SPOTIFY && searchInputValue) {
      searchSpotifyTracks(searchInputValue)
      return
    }

    if (platform === CampaignPlatformTypeEnum.TIKTOK) {
      void dispatch(searchTikTokTrackAuth({ searchText: searchInputValue }))
    }
  }
  const onTrackSearchHandler = useDebouncedCallback(
    ({ searchInput }: SearchTrackFormDataProps) => {
      if (!searchInput) {
        onClearTracks()
        return
      }
      platform && searchTracks({ searchInput })

      if (getIsTikTokTrackUrl(searchInput)) {
        platform && enteredTrackUrlEventHandler(platform)
      } else {
        platform && startedEnterTrackEventHandler(platform)
      }
    },
    DEBOUNCE_TIMER_1000,
  )

  const onCheckTrack = useDebouncedCallback((url: string) => {
    const isSpotifyPlatform = platform === CampaignPlatformTypeEnum.SPOTIFY
    const isTikTokPlatform = platform === CampaignPlatformTypeEnum.TIKTOK

    if (isSpotifyPlatform) {
      void dispatch(
        checkTrackToCreateCampaign({
          trackURL: `https://api.spotify.com/v1/tracks/${url}`,
        }),
      )
        .unwrap()
        .then(() => {
          navigate(
            `${ArtistRoutesEnum.ChooseCreateCampaignPlatform}/${platform}/${url}`,
          )
        })
        .catch(() => null)
    }

    if (isTikTokPlatform) {
      searchTikTokTrack({
        searchText: `https://www.tiktok.com/music/-${url}`,
      })
        .unwrap()
        .then(() => {
          navigate(
            `${ArtistRoutesEnum.ChooseCreateCampaignPlatform}/${platform}/${url}`,
          )
        })
        .catch(() => null)
    }
  }, DEBOUNCE_TIMER_300)

  const onChoseTikTokTrack = useDebouncedCallback(
    async (track: TikTokTrack) => {
      dispatch(setTikTokTrackFromSearchList(track))
      dispatch(setTiktokTracksToInitialState())
    },
    DEBOUNCE_TIMER_300,
  )

  const tracks =
    platform === CampaignPlatformTypeEnum.SPOTIFY ? spotifyTracks : tikTokTracks

  return {
    onClearTracks,
    existingTikTokCampaign,
    tikTokSearchErrorMessage,
    tikTokExistingCampaign,
    onCheckTrack,
    onChoseTikTokTrack,
    onTrackSearchHandler,
    onCancelSearch: onTrackSearchHandler.cancel,
    error: searchError || searchErrorSpotify || error || '',
    tracks: tracks || [],
    loading: isLoadingTikTok || isLoadingSpotify,
  }
}
